// ----------------------------------------------------------------------- imports externos
import { useCallback, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

// ----------------------------------------------------------------------- imports internos
import { useAuth } from "../../auth/AuthContext";
import { initCache } from "../../services/cache";
import {
  aplicarPrefs,
  guardarPrefsLocal,
  hayPrefsLocales,
  leerPrefs,
  PREF_DEF,
} from "../../functions/preferencias";
import { guardarPrefs, traerPrefs } from "../../services/preferencias";
import { traerConteos } from "../../services/conteos";
import { misPermisos } from "../../services/permisos";
import { leerVistaPrevia, puedeVerComo } from "../../services/verComo";
import VerComo from "./VerComo";
import Header from "./Header";
import Preferencias from "./Preferencias";
import BarraAbajo from "./BarraAbajo";
import Cabeza from "./Cabeza";
import SiSeRompe from "./SiSeRompe";
import PanelesFlotaBot from "./PanelesFlotaBot";
import Rail from "./Rail";
import "../../assets/css/hub.css";
// Las cinco piezas compartidas (tabla, pastilla, conteo, franja, lectura). Van
// acá y no en cada pantalla: el armazón lo monta todo el hub, así que basta
// una vez. Su gemelo vive en `estilo_comun.py` de FlotaBot — si tocás uno,
// tocá el otro.
import "../../assets/css/componentes.css";

/* El armazón: rail a la izquierda, cabecera arriba, la sección en el medio.
 *
 * El rail arranca plegado en pantallas angostas y abierto en escritorio. Se
 * decide una sola vez al montar y no se re-evalúa al rotar el celular: si
 * alguien lo abrió a mano, que una rotación se lo cierre es peor que quedar
 * con el estado "equivocado".
 */

const esAngosta = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(max-width: 820px)").matches;

const Layout = () => {
  const { fullUser } = useAuth();
  const { pathname, search } = useLocation();
  const navegar = useNavigate();
  const [plegado, setPlegado] = useState(esAngosta);
  const [prefsAbiertas, setPrefsAbiertas] = useState(false);
  const [cuentas, setCuentas] = useState({});
  // null = todavía no se sabe. Ver el comentario en Rail sobre por qué eso
  // no es lo mismo que "no tiene ninguno".
  const [permisos, setPermisos] = useState(null);
  /* Ver el hub como otra persona. Sobrevive a recargar la página: validar un
   * acceso implica abrir pantallas, y si cada navegación lo cancelara habría
   * que volver a elegir a la persona todo el tiempo. */
  const [viendoComo, setViendoComo] = useState(() => leerVistaPrevia());
  const puedeEspiar = puedeVerComo(fullUser?.mail);
  /* Sin permiso no hay vista previa, ni siquiera si quedó una guardada de
   * antes: los permisos pueden haber cambiado desde entonces. */
  const viendo = puedeEspiar ? viendoComo : null;

  useEffect(() => {
    if (!fullUser) return;
    initCache();
  }, [fullUser]);

  // Los números del menú. Van después de tener sesión: sin ella la política de
  // acceso los filtra a cero y quedarían todos en blanco para siempre.
  useEffect(() => {
    if (!fullUser) return;
    let vigente = true;
    traerConteos().then((c) => vigente && setCuentas(c));
    misPermisos().then((p) => vigente && setPermisos(p));
    return () => {
      vigente = false;
    };
  }, [fullUser]);

  /* Las preferencias son de cada persona, así que hay que esperar a saber quién
   * es. Hasta entonces valen las de por defecto.
   *
   * Dos pasos a propósito: primero se pinta con la copia del navegador, que es
   * instantánea, y recién después se pide la de la base. Al revés, cada carga
   * empieza en tema claro y salta a oscuro medio segundo más tarde.
   *
   * Manda la base. La copia local existe para el primer instante, no para
   * discutir. */
  useEffect(() => {
    if (!fullUser) return;
    let vigente = true;

    const local = leerPrefs(fullUser.id);
    aplicarPrefs(local);

    traerPrefs().then((remotas) => {
      if (!vigente) return;

      if (remotas) {
        const p = { ...PREF_DEF, ...remotas };
        aplicarPrefs(p);
        guardarPrefsLocal(fullUser.id, p);
        return;
      }

      // Todavía no hay nada guardado del lado del servidor. Si este navegador
      // tenía preferencias de antes —de cuando esto vivía sólo acá— se suben,
      // así nadie pierde lo que ya se había configurado. El fondo se sube
      // aparte desde el panel; acá va todo lo demás.
      if (hayPrefsLocales(fullUser.id)) {
        const { fondo, ...resto } = local;
        guardarPrefs(resto);
      }
    });

    return () => {
      vigente = false;
    };
    /* DEPENDE DEL id Y NO DEL OBJETO. `fullUser` se arma con un spread en cada
     * hidratación, así que es un objeto nuevo cada vez aunque la persona sea la
     * misma — y eso hacía que este efecto volviera a correr y a pedirle la
     * sesión a Supabase. Con el `TOKEN_REFRESHED` del otro lado se cerraba el
     * círculo: refresh → hidratar → `fullUser` nuevo → pedir sesión → refresh.
     * En los logs de auth, 32 vueltas en 61 segundos hasta el 429.
     *
     * El id es un string: si no cambió, no hay nada que volver a traer. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullUser?.id]);

  /* EL FONDO SE APAGA MIENTRAS DURA LA VISTA PREVIA. Se pidió así —el fondo
   * distrae cuando lo que se está mirando es qué baldosas hay— y de paso es la
   * señal que se ve de reojo, sin leer la franja, de que el hub que estás
   * mirando no es el tuyo. Al salir vuelve solo. */
  useEffect(() => {
    if (!viendo) return undefined;
    const previo = document.body.classList.contains("con-fondo");
    document.body.classList.remove("con-fondo");
    return () => {
      if (previo) document.body.classList.add("con-fondo");
    };
  }, [viendo]);

  // Qué sección del panel está abierta, si es que hay alguna.
  const enPanel = pathname.startsWith("/panel/");
  const seccionDelPanel = enPanel ? pathname.slice("/panel/".length) : null;
  // `?ver=` del buscador maestro: el ítem a abrir dentro del panel.
  const pedidoDelPanel = enPanel ? new URLSearchParams(search).get("ver") || "" : "";
  const pedidoTomado = useCallback(
    () => navegar(pathname, { replace: true }),
    [navegar, pathname],
  );

  const cerrarSiEsAngosta = () => {
    if (esAngosta()) setPlegado(true);
  };

  return (
    <>
      <div className={"hub" + (plegado ? " plegado" : "")}>
        <Rail
          plegado={plegado}
          /* Alterna: el mismo botón/ícono pliega cuando está abierto y ABRE
             cuando está colapsado. Antes forzaba `true` siempre, así que
             colapsado no se podía reabrir desde el propio rail. */
          onPlegar={() => setPlegado((p) => !p)}
          onNavegar={cerrarSiEsAngosta}
          cuentas={cuentas}
          permisos={viendo ? viendo.tiene : permisos}
        />
        {/* La columna del contenido tiene clase propia porque ahora hace algo:
            es la que se queda con el alto que sobra y le deja a `.hub-main` su
            propio desplazamiento. Ver el comentario de `.hub` en hub.css. */}
        <div className="hub-col">
          <Header
            plegado={plegado}
            onPlegar={() => setPlegado(false)}
            onAbrirPreferencias={() => setPrefsAbiertas(true)}
            /* Los mismos que ve el rail, así que el buscador respeta «Ver
               como»: previsualizar a alguien sin `ver.gente` y que el buscador
               siga ofreciendo personas haría que la vista previa mienta. */
            permisos={viendo ? viendo.tiene : permisos}
            verComo={
              puedeEspiar ? (
                <VerComo viendo={null} onCambiar={setViendoComo} />
              ) : null
            }
          />
          {/* La franja va DENTRO de la columna del contenido y pegada arriba:
              se ve en todas las pantallas y no tapa el rail. */}
          {viendo && <VerComo viendo={viendo} onCambiar={setViendoComo} />}
          <main className="hub-main">
            {/* El título de la pantalla, sacado de la misma lista que dibuja el
                menú. Va acá y no adentro de cada pantalla: escrito veinte veces
                a mano, el día que se renombre una el menú diría una cosa y su
                encabezado otra. */}
            <Cabeza />
            {/* En una sección del panel se muestra el iframe y no la ruta: los
                paneles viven acá, en el armazón, para que sigan cargados
                cuando alguien se va a otra pantalla y vuelve. */}
            {/* Las cuentas y los permisos viajan por el contexto del Outlet y
                no como props: el armazón ya los tiene pedidos para el menú, y
                que cada pantalla los vuelva a pedir serían dos consultas más
                en el arranque, que es justo donde se notan. `permisos` es el
                mismo que ve el rail, así que respeta «Ver como». */}
            {/* Si una pantalla se rompe, que se rompa SOLA. Envuelve el
                CONTENIDO y no el armazón: el rail y la cabecera quedan afuera,
                así que la persona se va a otra pantalla y sigue trabajando.
                `key` con la ruta lo remonta al cambiar de pantalla — sin eso el
                cartel de error se quedaría pegado para siempre. */}
            {enPanel ? null : (
              <SiSeRompe key={pathname}>
                <Outlet
                  context={{
                    cuentas,
                    permisos: viendo ? viendo.tiene : permisos,
                    /* Para las pantallas que le preguntan algo a FlotaBot por
                       quien mira («¿Cómo se usa?»): mismo dato que cruza el
                       iframe de los paneles. */
                    verComoMail: viendo?.mail || null,
                  }}
                />
              </SiSeRompe>
            )}
            {/* La vista previa cruza el iframe. Sin esto, «Ver como» recorta
                el menú del hub y los paneles de FlotaBot siguen dibujándose
                con la sesión propia — o sea que la herramienta contesta una
                cosa de un lado y otra del otro sobre el mismo permiso. */}
            <PanelesFlotaBot
              seccion={seccionDelPanel}
              verComoMail={viendo?.mail || null}
              pedido={pedidoDelPanel}
              onPedidoTomado={pedidoTomado}
              /* Para el «Editar» de los artículos de una sede: el panel le
                 pide al hub el editor de Stock y la puerta es este permiso. */
              permisos={viendo ? viendo.tiene : permisos}
            />
          </main>
        </div>
      </div>

      {/* La pestaña flotante para reabrir el rail ya no va: colapsado, el rail
          queda como una tira de íconos con su propio botón para reabrir. */}

      {/* En el celular el rail se superpone al contenido, así que necesita un
          velo que lo cierre al tocar afuera. */}
      {!plegado && <div className="hub-velo" onClick={() => setPlegado(true)} />}

      {/* La barra de abajo, sólo en angosto (la esconde una `@media`). Va
          FUERA de la grilla, igual que la pestaña de reabrir el rail: pegada a
          la ventana y no al contenido, que se desplaza por su cuenta. */}
      <BarraAbajo
        permisos={viendo ? viendo.tiene : permisos}
        onMenu={() => setPlegado(false)}
        onNavegar={cerrarSiEsAngosta}
      />

      <Preferencias
        abierto={prefsAbiertas}
        onCerrar={() => setPrefsAbiertas(false)}
        idPersona={fullUser?.id}
        nombrePersona={fullUser?.nombres}
      />
    </>
  );
};

export default Layout;
